import React, { useRef, useState, useEffect } from "react";
import { BrowserMultiFormatReader, NotFoundException } from "@zxing/library";

export default function BarcodeScanner({ onScanResult }) {
    const videoRef = useRef(null);
    const codeReaderRef = useRef(null);
    const streamRef = useRef(null);

    const [scanActive, setScanActive] = useState(false);
    const [cameraActive, setCameraActive] = useState(false);

    // Состояния для ручного ввода
    const [showManualInput, setShowManualInput] = useState(false);
    const [manualBarcode, setManualBarcode] = useState('');

    // Базовые размеры (соотношение 2:1)
    const rectWidth = 280;
    const rectHeight = 140;

    // Определяем мобайл, чтобы включать full-bleed (100vw)
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia?.("(max-width: 600px)");
        const set = () => setIsMobile(Boolean(mq?.matches));
        set();
        mq?.addEventListener?.("change", set);
        window.addEventListener("resize", set);
        return () => {
            mq?.removeEventListener?.("change", set);
            window.removeEventListener("resize", set);
        };
    }, []);

    // Остановить поток + декодер
    const stopCamera = () => {
        console.log("🔴 [CAMERA] Начинаем остановку камеры...");

        try {
            if (codeReaderRef.current) {
                console.log("🔴 [CAMERA] Останавливаем декодер...");
                codeReaderRef.current.stopContinuousDecode?.();
                codeReaderRef.current.reset?.();
                codeReaderRef.current = null;
                console.log("✅ [CAMERA] Декодер остановлен");
            }
        } catch (e) {
            console.warn("⚠️ [CAMERA] Ошибка остановки декодера:", e);
        }

        if (streamRef.current) {
            console.log("🔴 [CAMERA] Останавливаем поток...");
            streamRef.current.getTracks().forEach((track, index) => {
                console.log(`🔴 [CAMERA] Останавливаем трек ${index}:`, track.kind, track.label);
                track.stop();
            });
            streamRef.current = null;
            console.log("✅ [CAMERA] Поток остановлен");
        }

        if (videoRef.current) {
            console.log("🔴 [CAMERA] Очищаем видео элемент...");
            videoRef.current.srcObject = null;
            console.log("✅ [CAMERA] Видео элемент очищен");
        }

        setScanActive(false);
        setCameraActive(false);
        console.log("✅ [CAMERA] Камера полностью выключена");
    };

    // Запуск камеры с подробным логированием
    const startCamera = async () => {
        console.log("🟢 [CAMERA] ======== НАЧИНАЕМ ВКЛЮЧЕНИЕ КАМЕРЫ ========");

        try {
            console.log("🟢 [CAMERA] Шаг 1: Останавливаем предыдущую камеру");
            stopCamera();

            console.log("🟢 [CAMERA] Шаг 2: Настраиваем ограничения камеры");
            const constraints = {
                video: {
                    facingMode: { ideal: "environment" },
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    frameRate: { ideal: 30 },
                },
                audio: false,
            };
            console.log("🟢 [CAMERA] Ограничения:", constraints);

            console.log("🟢 [CAMERA] Шаг 3: Запрашиваем доступ к камере...");
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            console.log("✅ [CAMERA] Поток получен:", stream);
            console.log("🟢 [CAMERA] Треки потока:", stream.getTracks().map(track => ({
                kind: track.kind,
                label: track.label,
                enabled: track.enabled,
                readyState: track.readyState
            })));

            streamRef.current = stream;

            console.log("🟢 [CAMERA] Шаг 4: Настраиваем видео элемент");
            const video = videoRef.current;
            if (!video) {
                throw new Error("Видео элемент не найден!");
            }

            console.log("🟢 [CAMERA] Видео элемент найден:", video);
            console.log("🟢 [CAMERA] Текущее состояние видео:", {
                readyState: video.readyState,
                paused: video.paused,
                muted: video.muted,
                autoplay: video.autoplay
            });

            video.srcObject = stream;
            video.muted = true;
            video.playsInline = true;
            video.autoplay = true;

            console.log("🟢 [CAMERA] Шаг 5: Устанавливаем стили видео");
            video.style.width = "100%";
            video.style.height = "100%";
            video.style.objectFit = "cover";
            video.style.display = "block";
            console.log("✅ [CAMERA] Стили применены");

            console.log("🟢 [CAMERA] Шаг 6: Запускаем воспроизведение");
            const playVideo = async () => {
                console.log("▶️ [VIDEO] Попытка запуска воспроизведения...");
                console.log("▶️ [VIDEO] Состояние перед запуском:", {
                    readyState: video.readyState,
                    paused: video.paused,
                    videoWidth: video.videoWidth,
                    videoHeight: video.videoHeight,
                    currentTime: video.currentTime
                });

                try {
                    await video.play();
                    console.log("✅ [VIDEO] Видео запущено успешно!");
                    console.log("✅ [VIDEO] Состояние после запуска:", {
                        readyState: video.readyState,
                        paused: video.paused,
                        videoWidth: video.videoWidth,
                        videoHeight: video.videoHeight,
                        currentTime: video.currentTime
                    });

                    // Дополнительная проверка через задержку
                    setTimeout(() => {
                        console.log("🔍 [VIDEO] Проверка состояния через 200ms:", {
                            readyState: video.readyState,
                            paused: video.paused,
                            videoWidth: video.videoWidth,
                            videoHeight: video.videoHeight,
                            currentTime: video.currentTime
                        });

                        if (video.paused || video.readyState < 2) {
                            console.log("🔄 [VIDEO] Видео требует перезапуска...");
                            video.play().catch(e => console.error("❌ [VIDEO] Ошибка перезапуска:", e));
                        } else {
                            console.log("✅ [VIDEO] Видео работает корректно!");
                        }
                    }, 200);

                } catch (e) {
                    console.error("❌ [VIDEO] Автовоспроизведение заблокировано:", e);
                    console.log("💡 [VIDEO] Пользователю нужно кликнуть по видео для запуска");
                }
            };

            console.log("🟢 [CAMERA] Шаг 7: Ждем загрузки метаданных");
            if (video.readyState >= 1) {
                console.log("✅ [CAMERA] Метаданные уже загружены");
                await playVideo();
            } else {
                console.log("⏳ [CAMERA] Ждем событие loadedmetadata...");
                video.addEventListener("loadedmetadata", async () => {
                    console.log("✅ [CAMERA] Метаданные загружены!");
                    console.log("✅ [CAMERA] Размеры видео:", {
                        videoWidth: video.videoWidth,
                        videoHeight: video.videoHeight
                    });
                    await playVideo();
                }, { once: true });
            }

            // Добавляем обработчик клика для принудительного запуска
            console.log("🟢 [CAMERA] Шаг 8: Добавляем обработчик клика");
            video.addEventListener('click', () => {
                console.log("🖱️ [VIDEO] Клик по видео");
                if (video.paused) {
                    console.log("▶️ [VIDEO] Запускаем видео по клику...");
                    video.play().catch(e => console.error("❌ [VIDEO] Ошибка запуска по клику:", e));
                }
            }, { once: true });

            setCameraActive(true);
            console.log("✅ [CAMERA] ======== КАМЕРА УСПЕШНО АКТИВИРОВАНА ========");

        } catch (err) {
            console.error("❌ [CAMERA] ======== ОШИБКА ВКЛЮЧЕНИЯ КАМЕРЫ ========");
            console.error("❌ [CAMERA] Детали ошибки:", err);
            console.error("❌ [CAMERA] Стек ошибки:", err.stack);
            setCameraActive(false);
        }
    };

    const toggleCamera = () => {
        if (cameraActive) stopCamera();
        else startCamera();
    };

    const toggleScan = () => {
        console.log("🔍 [SCAN] Переключение сканирования...");

        if (scanActive) {
            console.log("🔍 [SCAN] Останавливаем сканирование...");
            try {
                if (codeReaderRef.current) {
                    codeReaderRef.current.stopContinuousDecode?.();
                    codeReaderRef.current.reset?.();
                    codeReaderRef.current = null;
                }
                console.log("✅ [SCAN] Сканирование остановлено");
            } catch (e) {
                console.warn("⚠️ [SCAN] Ошибка остановки сканирования:", e);
            }
            setScanActive(false);

            // ВАЖНО: Перезапускаем видео после остановки сканирования
            console.log("🔄 [SCAN] Перезапускаем видеопоток...");
            const video = videoRef.current;
            if (video && streamRef.current) {
                // Принудительно показываем видео
                video.style.display = "block";
                console.log("🔄 [SCAN] Принудительно показываем видео: display = block");

                // Переподключаем поток к видео
                video.srcObject = streamRef.current;
                // Принудительно запускаем воспроизведение
                video.play().catch(e => {
                    console.warn("⚠️ [SCAN] Не удалось перезапустить видео:", e);
                });
                console.log("✅ [SCAN] Видеопоток перезапущен");
            }

        } else if (cameraActive) {
            console.log("🔍 [SCAN] Запускаем сканирование...");
            setScanActive(true);
        }
    };

    // Функция для показа окна ручного ввода
    const showManualInputDialog = () => {
        setShowManualInput(true);
        setManualBarcode('');
    };

    // Функция для закрытия окна ручного ввода
    const closeManualInput = () => {
        setShowManualInput(false);
        setManualBarcode('');
    };

    // Функция для обработки ручного ввода
    const handleManualInput = () => {
        const trimmedBarcode = manualBarcode.trim();

        if (!trimmedBarcode) {
            alert('Введіть штрихкод');
            return;
        }

        console.log('Ручне введення штрихкода:', trimmedBarcode);

        // Передаем только штрихкод родительскому компоненту
        if (onScanResult) {
            onScanResult(trimmedBarcode);
        }

        closeManualInput();
    };

    // Чистка при размонтировании
    useEffect(() => () => stopCamera(), []);

    // Управление отображением видео через состояние камеры
    useEffect(() => {
        const video = videoRef.current;
        if (video) {
            if (cameraActive) {
                console.log("👁️ [DISPLAY] Показываем видео - камера активна");
                video.style.display = "block";
            } else {
                console.log("👁️ [DISPLAY] Скрываем видео - камера неактивна");
                video.style.display = "none";
            }
        }
    }, [cameraActive]);

    // Запуск/остановка только декодирования (видео уже крутится)
    useEffect(() => {
        if (!scanActive || !cameraActive) return;

        const reader = new BrowserMultiFormatReader();
        codeReaderRef.current = reader;
        const scanningRef = { current: false }; // Защита от повторного сканирования

        const startDecode = async () => {
            try {
                console.log("🔍 [SCAN] Запускаем декодирование...");

                await reader.decodeFromVideoDevice(
                    null,
                    videoRef.current,
                    (result, err) => {
                        if (result && !scanningRef.current) {
                            scanningRef.current = true;
                            console.log("✅ [SCAN] Штрих-код найден:", result.getText());
                            const text = result.getText();
                            onScanResult?.(text);

                            // ВЫКЛЮЧАЕМ режим сканирования при успешном сканировании
                            console.log("🔴 [SCAN] Выключаем режим сканирования после успешного результата");

                            // Останавливаем декодер
                            try {
                                if (codeReaderRef.current) {
                                    codeReaderRef.current.stopContinuousDecode?.();
                                    codeReaderRef.current.reset?.();
                                    codeReaderRef.current = null;
                                }
                            } catch (e) {
                                console.warn("⚠️ [SCAN] Ошибка остановки декодера:", e);
                            }

                            // Выключаем состояние сканирования
                            setScanActive(false);

                            // Перезапускаем видеопоток для корректного отображения
                            const video = videoRef.current;
                            if (video && streamRef.current) {
                                video.style.display = "block";
                                video.srcObject = streamRef.current;
                                video.play().catch(e => {
                                    console.warn("⚠️ [SCAN] Не удалось перезапустить видео:", e);
                                });
                            }

                            console.log("✅ [SCAN] Режим сканирования успешно выключен");
                        }
                        if (err && !(err instanceof NotFoundException)) {
                            console.warn("⚠️ [SCAN] Ошибка сканирования:", err);
                        }
                    }
                );

                console.log("✅ [SCAN] Декодирование активно");
            } catch (e) {
                console.error("❌ [SCAN] Не удалось запустить декодирование:", e);
            }
        };

        startDecode();

        return () => {
            console.log("🔄 [SCAN] Очистка useEffect - останавливаем декодер");
            try { reader.stopContinuousDecode?.(); } catch {}
            try { reader.reset?.(); } catch {}
        };
    }, [scanActive, cameraActive, onScanResult]);

    // -------- стили для мобильной адаптации --------
    const videoBoxStyle = {
        position: "relative",
        width: "100%",
        maxWidth: "400px",
        aspectRatio: `${rectWidth} / ${rectHeight}`,
        margin: "8px auto",
        border: "2px solid blue",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: cameraActive ? "transparent" : "#f0f0f0",
    };

    const buttonsWrapStyle = {
        textAlign: "center",
        padding: "8px",
        width: "100%",
        maxWidth: "400px",
        margin: "0 auto",
    };

    return (
        <div style={{ textAlign: "center", padding: "8px" }}>
            {/* Блок видеопотока */}
            <div style={videoBoxStyle}>
                <video
                    ref={videoRef}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                    }}
                    autoPlay
                    muted
                    playsInline
                />
                {!cameraActive && (
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#e0e0e0",
                            color: "#666",
                            fontSize: "12px",
                        }}
                    >
                        Камера вимкнена
                    </div>
                )}
            </div>

            {/* Кнопки */}
            <div style={buttonsWrapStyle}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: isMobile ? "4px" : "8px",
                        flexWrap: "nowrap", // Предотвращаем перенос на новую строку
                        alignItems: "center",
                    }}
                >
                    {/* Кнопка включить/отключить камеру */}
                    <button
                        style={{
                            padding: isMobile ? "6px 8px" : "8px 12px",
                            fontSize: isMobile ? "18px" : "24px",
                            background: cameraActive ? "#e74c3c" : "#27ae60",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            minWidth: isMobile ? "60px" : "80px",
                            minHeight: isMobile ? "36px" : "44px",
                            fontWeight: "600",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            transition: "all 0.2s ease",
                            flex: "1 1 auto",
                            maxWidth: isMobile ? "80px" : "120px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "1px"
                        }}
                        onClick={toggleCamera}
                        title={cameraActive ? "Вимкнути камеру" : "Увімкнути камеру"}
                    >
                        <span style={{ fontSize: isMobile ? "14px" : "16px" }}>📷</span>
                        <span style={{ fontSize: isMobile ? "8px" : "9px", fontWeight: "bold" }}>
                            {cameraActive ? "ВИМКН" : "УВІМКН"}
                        </span>
                    </button>

                    {/* Кнопка сканировать/остановить скан */}
                    <button
                        style={{
                            padding: isMobile ? "6px 8px" : "8px 12px",
                            fontSize: isMobile ? "18px" : "24px",
                            background: scanActive ? "#f39c12" : "#3498db",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            opacity: cameraActive ? 1 : 0.5,
                            minWidth: isMobile ? "60px" : "80px",
                            minHeight: isMobile ? "36px" : "44px",
                            fontWeight: "600",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            transition: "all 0.2s ease",
                            flex: "1 1 auto",
                            maxWidth: isMobile ? "80px" : "120px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "1px"
                        }}
                        onClick={toggleScan}
                        disabled={!cameraActive}
                        title={scanActive ? "Зупинити сканування" : "Почати сканування"}
                    >
                        <span style={{ fontSize: isMobile ? "14px" : "16px" }}>🔍</span>
                        <span style={{ fontSize: isMobile ? "8px" : "9px", fontWeight: "bold" }}>
                            {scanActive ? "СТОП" : "СКАН"}
                        </span>
                    </button>

                    {/* Кнопка ручного ввода (бывшая ТЕСТ) */}
                    <button
                        style={{
                            padding: isMobile ? "6px 6px" : "8px 12px",
                            fontSize: isMobile ? "18px" : "24px",
                            background: "#9b59b6",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            minWidth: isMobile ? "60px" : "80px",
                            minHeight: isMobile ? "36px" : "44px",
                            fontWeight: "600",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            transition: "all 0.2s ease",
                            flex: "1 1 auto",
                            maxWidth: isMobile ? "80px" : "120px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "1px"
                        }}
                        onClick={showManualInputDialog}
                        title="Ручне введення штрихкода"
                    >
                        <span style={{ fontSize: isMobile ? "14px" : "16px" }}>✍️</span>
                        <span style={{ fontSize: isMobile ? "7px" : "9px", fontWeight: "bold" }}>
                            РУЧНОЙ
                        </span>
                    </button>
                </div>
            </div>

            {/* Модальное окно для ручного ввода */}
            {showManualInput && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'white',
                        padding: '20px',
                        borderRadius: '12px',
                        maxWidth: '400px',
                        width: '90%',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '20px',
                            borderBottom: '1px solid #eee',
                            paddingBottom: '10px'
                        }}>                            <h3 style={{ margin: 0, color: '#333', fontSize: '18px' }}>
                                ✍️ Ручне введення штрихкода
                            </h3>
                            <button
                                onClick={closeManualInput}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#666',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#333'
                            }}>
                                Штрихкод:
                            </label>
                            <input
                                type="text"
                                value={manualBarcode}
                                onChange={(e) => setManualBarcode(e.target.value)}
                                placeholder="Введіть штрихкод"
                                autoFocus
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    fontSize: '16px',
                                    border: '2px solid #e9ecef',
                                    borderRadius: '6px',
                                    outline: 'none',
                                    fontFamily: 'monospace',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#007bff';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e9ecef';
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleManualInput();
                                    }
                                }}
                            />
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '10px',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                onClick={closeManualInput}
                                style={{
                                    padding: '10px 20px',
                                    fontSize: '14px',
                                    color: '#6c757d',
                                    background: '#fff',
                                    border: '2px solid #e9ecef',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                Отмена
                            </button>
                            <button
                                onClick={handleManualInput}
                                disabled={!manualBarcode.trim()}
                                style={{
                                    padding: '10px 20px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#fff',
                                    background: !manualBarcode.trim() ? '#6c757d' : '#28a745',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: !manualBarcode.trim() ? 'not-allowed' : 'pointer'
                                }}
                            >
                                ✅ Підтвердити
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
